import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SaveIcon from '@mui/icons-material/Save';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import getLetterTemplateQuery from '../../../../Apollo/queries/getLetterTemplate';
import GET_MAIL_SENDER from '../../../../Apollo/queries/getMailSender';
import createLetterTemplateMutation from '../../../../Apollo/mutations/createLetterTemplate';
import updateLetterTemplateMutation from '../../../../Apollo/mutations/updateLetterTemplate';
import { PagePathname } from '../../../../routes/paths';

// Placeholder categories
const PLACEHOLDER_CATEGORIES = [
  {
    label: 'Persönliche Daten',
    placeholders: ['VORNAME', 'NACHNAME', 'GEBURTSTAG', 'GESCHLECHT', 'STATUS'],
  },
  {
    label: 'Kontaktdaten',
    placeholders: ['TELEFON', 'MOBILTELEFON', 'FAX', 'GESCHAEFTSEMAIL', 'PRIVATEEMAIL'],
  },
  {
    label: 'Adressdaten',
    placeholders: ['STRASSE', 'PLZ', 'ORT', 'LAND'],
  },
  {
    label: 'Abweichende Rechnungsadresse',
    placeholders: ['RA_STRASSE', 'RA_PLZ', 'RA_ORT', 'RA_LAND'],
  },
  {
    label: 'Vertrag & Firma',
    placeholders: [
      'RANG',
      'LEVEL',
      'FIRMA',
      'VERTRAGSART',
      'BERATERNUMMER',
      'MYJACK_UNIT',
      'MYJACK_LOGIN',
      'MANDATSREFERENZ',
    ],
  },
  {
    label: 'System & Buchhaltung',
    placeholders: ['KREDITORENNUMMER', 'STEUERNUMMER', 'IBAN', 'BANKNAME', 'BIC', 'STARTDATUM', 'TEAMLEITER'],
  },
];

// Whitelist of safe inline/block tags that may survive a paste.
// Everything else is either dropped completely (script, style, meta, ...) or
// unwrapped to its children so foreign formatting (background colors,
// font-families, Word/Google-Docs span wrappers, ...) cannot sneak in.
const SAFE_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'P', 'BR', 'DIV', 'UL', 'OL', 'LI', 'SPAN', 'A']);
const DROP_COMPLETELY = new Set(['SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD', 'IFRAME', 'OBJECT']);

function sanitizePastedNode(node: Node, doc: Document): Node | DocumentFragment | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent ?? '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toUpperCase();
  if (DROP_COMPLETELY.has(tag)) {
    return null;
  }
  if (!SAFE_TAGS.has(tag)) {
    const frag = doc.createDocumentFragment();
    el.childNodes.forEach((child) => {
      const sanitized = sanitizePastedNode(child, doc);
      if (sanitized) frag.appendChild(sanitized);
    });
    return frag;
  }
  const clean = doc.createElement(tag.toLowerCase());
  el.childNodes.forEach((child) => {
    const sanitized = sanitizePastedNode(child, doc);
    if (sanitized) clean.appendChild(sanitized);
  });
  return clean;
}

function sanitizePastedHtml(html: string): DocumentFragment {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const fragment = document.createDocumentFragment();
  parsed.body.childNodes.forEach((child) => {
    const sanitized = sanitizePastedNode(child, document);
    if (sanitized) fragment.appendChild(sanitized);
  });
  return fragment;
}

function plainTextToFragment(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const lines = text.split(/\r?\n/);
  lines.forEach((line, idx) => {
    if (line) fragment.appendChild(document.createTextNode(line));
    if (idx < lines.length - 1) fragment.appendChild(document.createElement('br'));
  });
  return fragment;
}

// Example data for live preview
const EXAMPLE_DATA: Record<string, string> = {
  VORNAME: 'Max',
  NACHNAME: 'Mustermann',
  GEBURTSTAG: '15.03.1985',
  GESCHLECHT: 'männlich',
  STATUS: 'Aktiv',
  TELEFON: '+49 30 123456',
  MOBILTELEFON: '+49 170 1234567',
  FAX: '+49 30 123457',
  GESCHAEFTSEMAIL: 'max.mustermann@example.de',
  PRIVATEEMAIL: 'max.privat@example.de',
  STRASSE: 'Musterstraße 42',
  PLZ: '10115',
  ORT: 'Berlin',
  LAND: 'Deutschland',
  RA_STRASSE: 'Rechnungsweg 7',
  RA_PLZ: '10117',
  RA_ORT: 'Berlin',
  RA_LAND: 'Deutschland',
  RANG: 'Gold',
  LEVEL: 'Senior',
  FIRMA: 'Mustermann GmbH',
  VERTRAGSART: 'Standard',
  BERATERNUMMER: 'B-2026-0042',
  MYJACK_UNIT: 'Unit-Berlin',
  MYJACK_LOGIN: 'mmustermann',
  KREDITORENNUMMER: 'K-100042',
  STEUERNUMMER: '27/123/45678',
  IBAN: 'DE89 3704 0044 0532 0130 00',
  BANKNAME: 'Commerzbank',
  BIC: 'COBADEFFXXX',
  STARTDATUM: '01.01.2026',
  TEAMLEITER: 'Anna Schmidt',
  MANDATREFERENZ: 'MR-2026-0042',
  MANDATSREFERENZ: 'MR-2026-0042',
};

function LetterTemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id !== undefined && id !== 'neu';
  const editorRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [nameError, setNameError] = useState('');
  const [contentError, setContentError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const { data: templateData } = useQuery(getLetterTemplateQuery, {
    variables: { id: parseInt(id || '0', 10) },
    skip: !isEditMode,
    fetchPolicy: 'network-only',
  });
  const { data: mailSenderData } = useQuery(GET_MAIL_SENDER, { fetchPolicy: 'network-only' });

  const [createTemplate] = useMutation(createLetterTemplateMutation);
  const [updateTemplate] = useMutation(updateLetterTemplateMutation);

  useEffect(() => {
    if (templateData?.getLetterTemplate) {
      const t = templateData.getLetterTemplate;
      setName(t.name || '');
      setDescription(t.description || '');
      setContent(t.content || '');
      // Set editor content
      if (editorRef.current) {
        editorRef.current.innerHTML = t.content || '';
      }
    }
  }, [templateData]);

  const handleEditorInput = useCallback(() => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
      setIsDirty(true);
    }
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;

    const html = e.clipboardData.getData('text/html');
    const plain = e.clipboardData.getData('text/plain');
    const fragment = html ? sanitizePastedHtml(html) : plainTextToFragment(plain);
    if (!fragment.firstChild) return;

    const lastNode = fragment.lastChild;
    const selection = window.getSelection();

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editor.contains(range.commonAncestorContainer)) {
        range.deleteContents();
        range.insertNode(fragment);
        if (lastNode) {
          range.setStartAfter(lastNode);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        editor.appendChild(fragment);
      }
    } else {
      editor.appendChild(fragment);
    }

    setContent(editor.innerHTML);
    setIsDirty(true);
  }, []);

  const insertPlaceholder = (placeholder: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      // If no selection, append at the end
      editor.innerHTML += `{{${placeholder}}}`;
    } else {
      const range = selection.getRangeAt(0);
      // Only insert if the cursor is inside the editor
      if (editor.contains(range.commonAncestorContainer)) {
        const textNode = document.createTextNode(`{{${placeholder}}}`);
        range.deleteContents();
        range.insertNode(textNode);
        range.setStartAfter(textNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editor.innerHTML += `{{${placeholder}}}`;
      }
    }
    setContent(editor.innerHTML);
    setIsDirty(true);
  };

  const execCommand = (command: string) => {
    document.execCommand(command, false);
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  const extractVariables = (text: string): string[] => {
    const matches = text.match(/\{\{(\w+)\}\}/g);
    if (!matches) return [];
    return Array.from(new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, ''))));
  };

  const replaceVariablesForPreview = (html: string): string =>
    html.replace(/\{\{(\w+)\}\}/g, (_, varName) => EXAMPLE_DATA[varName] || `{{${varName}}}`);

  const validate = (): boolean => {
    let valid = true;
    setNameError('');
    setContentError('');

    if (!name.trim()) {
      setNameError('Bitte einen Namen eingeben');
      valid = false;
    }

    const plainContent = editorRef.current?.textContent || '';
    if (!plainContent.trim()) {
      setContentError('Bitte einen Inhalt eingeben');
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const editorContent = editorRef.current?.innerHTML || content;
    const variables = extractVariables(editorContent);

    const input = {
      name: name.trim(),
      description: description.trim() || null,
      content: editorContent,
      variables_used: variables.length > 0 ? variables : null,
    };

    try {
      if (isEditMode) {
        await updateTemplate({
          variables: { input, id: parseInt(id!, 10) },
        });
      } else {
        await createTemplate({
          variables: { input },
        });
      }
      navigate(PagePathname.briefvorlagen);
    } catch (err: unknown) {
      const error = err as { message?: string };
      if (error.message?.includes('DUPLICATE_NAME') || error.message?.includes('existiert bereits')) {
        setNameError('Eine Vorlage mit diesem Namen existiert bereits');
      }
    }
  };

  const handleCancel = () => {
    if (isDirty || name || description || content) {
      setDiscardDialogOpen(true);
    } else {
      navigate(PagePathname.briefvorlagen);
    }
  };

  const today = new Date();
  const formattedDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <Box
            component="span"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(PagePathname.einstellung)}
          >
            Einstellungen
          </Box>
          {' › '}
          <Box
            component="span"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={handleCancel}
          >
            Vorlagen
          </Box>
          {' › '}
          <Box component="span" sx={{ color: 'text.primary' }}>
            {isEditMode ? name || 'Bearbeiten' : 'Neue Vorlage'}
          </Box>
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditMode ? (
            <EditIcon sx={{ fontSize: 28, color: '#4ADE80' }} />
          ) : (
            <MailOutlineIcon sx={{ fontSize: 28, color: '#4ADE80' }} />
          )}
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {isEditMode ? name || 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={handleCancel} sx={{ color: 'text.secondary', borderColor: 'divider' }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{
              bgcolor: '#4ADE80',
              color: '#000',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#3CC970' },
            }}
          >
            Speichern
          </Button>
        </Box>
      </Box>

      {/* Split View */}
      <Box sx={{ display: 'flex', gap: 3, minHeight: 600 }}>
        {/* Left Column: Form */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Template Name */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              mb: 0.5,
              display: 'block',
            }}
          >
            Vorlagenname
          </Typography>
          <TextField
            fullWidth
            placeholder="z.B. Willkommensschreiben Bistro"
            value={name}
            onChange={(e) => {
              setName(e.target.value.substring(0, 120));
              setIsDirty(true);
              setNameError('');
            }}
            error={!!nameError}
            helperText={nameError}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 120 }}
          />

          {/* Description */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              mb: 0.5,
              display: 'block',
            }}
          >
            Interne Beschreibung (optional)
          </Typography>
          <TextField
            fullWidth
            placeholder="Kurznotiz für das Team..."
            value={description}
            onChange={(e) => {
              setDescription(e.target.value.substring(0, 250));
              setIsDirty(true);
            }}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 250 }}
          />

          {/* Placeholders */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              mb: 1,
              display: 'block',
            }}
          >
            Platzhalter einfügen
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {PLACEHOLDER_CATEGORIES.map((cat) =>
              cat.placeholders.map((ph) => (
                <Tooltip key={ph} title={cat.label} arrow placement="top">
                  <Chip
                    label={`{{${ph}}}`}
                    onClick={() => insertPlaceholder(ph)}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(74, 222, 128, 0.15)',
                      color: '#4ADE80',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(74, 222, 128, 0.3)' },
                    }}
                  />
                </Tooltip>
              )),
            )}
          </Box>

          {/* Text Editor */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              mb: 0.5,
              display: 'block',
            }}
          >
            Inhalt / Text
          </Typography>

          {/* Toolbar */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              p: 0.5,
              borderRadius: '4px 4px 0 0',
              border: '1px solid',
              borderColor: contentError ? 'error.main' : 'divider',
              borderBottom: 'none',
              bgcolor: 'action.hover',
            }}
          >
            <Tooltip title="Fett">
              <IconButton size="small" onClick={() => execCommand('bold')}>
                <FormatBoldIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Kursiv">
              <IconButton size="small" onClick={() => execCommand('italic')}>
                <FormatItalicIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Unterstrichen">
              <IconButton size="small" onClick={() => execCommand('underline')}>
                <FormatUnderlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Linksbündig ausrichten">
              <IconButton size="small" onClick={() => execCommand('justifyLeft')}>
                <FormatAlignLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Aufzählung">
              <IconButton size="small" onClick={() => execCommand('insertUnorderedList')}>
                <FormatListBulletedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Editor Area */}
          <Box
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleEditorInput}
            onPaste={handlePaste}
            sx={{
              minHeight: 200,
              p: 2,
              border: '1px solid',
              borderColor: contentError ? 'error.main' : 'divider',
              borderRadius: '0 0 4px 4px',
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '13px',
              lineHeight: 1.7,
              outline: 'none',
              resize: 'vertical',
              overflow: 'auto',
              '&:focus': {
                borderColor: '#4ADE80',
              },
            }}
          />
          {contentError && (
            <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
              {contentError}
            </Typography>
          )}
        </Box>

        {/* Right Column: Live Preview */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: 'background.default',
            borderRadius: 2,
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#4ADE80' }} />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                letterSpacing: 1,
              }}
            >
              Live-Vorschau (Beispieldaten)
            </Typography>
          </Box>

          {/* Preview Box */}
          <Box
            data-testid="letter-template-preview-card"
            sx={{
              bgcolor: '#ffffff',
              borderRadius: 2,
              p: 4,
              color: '#1a1a1a',
              minHeight: 400,
            }}
          >
            {/* Letterhead */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1.25rem', color: '#1a1a1a' }}>rita.ag</Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                  {mailSenderData?.getMailSender ?? ''}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>{formattedDate}</Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3, bgcolor: '#e0e0e0' }} />

            {/* Preview Content */}
            <Box
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '13px',
                lineHeight: 1.7,
                color: '#333',
                '& p': { marginBottom: '0.5em', marginTop: 0 },
                '& ul': { paddingLeft: '1.5em' },
              }}
              dangerouslySetInnerHTML={{
                __html: replaceVariablesForPreview(content),
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Discard Dialog */}
      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
        <DialogTitle>Bearbeitung abbrechen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Bearbeitung abbrechen? Alle nicht gespeicherten Eingaben gehen verloren.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDiscardDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#4ADE80',
              color: '#000',
              '&:hover': { bgcolor: '#3CC970' },
            }}
          >
            Weiterbearbeiten
          </Button>
          <Button
            onClick={() => navigate(PagePathname.briefvorlagen)}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Verwerfen und zurück
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LetterTemplateForm;
